import DragHandle from '@tiptap/extension-drag-handle-react';
import Highlight from '@tiptap/extension-highlight';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import { Placeholder } from '@tiptap/extensions';
import { Node } from '@tiptap/pm/model';
import { EditorState } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { Content, Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { FunctionComponent, memo, MutableRefObject, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StoryCardPostContentBlockFields } from '../../../services';
import { FileAcceptType } from '../../hooks';
import { htmlToMarkdown } from '../../utils';
import { IconLabel } from '../IconLabel';
import { BlockTools, TableMenu, TableMenuSettings } from './BlockTools';
import { InlineTools } from './InlineTools';
import {
  AudioNode,
  CalloutNode,
  CodeNode,
  DelimiterNode,
  EmbedLinkNode,
  FileNode,
  ImageNode,
  PdfNode,
  QuoteNode,
  StoryCardNode,
  StoryNode,
  UserNode,
  VideoNode,
} from './Nodes';

//eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import { BubbleMenu } from '@tiptap/react/menus';

import classNames from 'classnames';
import classes from './TextEditor.module.scss';

interface TextEditorProps {
  content: Content;
  editorRef?: MutableRefObject<Editor | null>;
  onUpdateHandler?: (content: string | StoryCardPostContentBlockFields[]) => void;
  onPasteHandler?: (text: string) => void;
  isShortPost?: boolean;
}

interface DropPasteMediaHandlerArgs {
  view: EditorView;
  event: Event;
  files: File[];
}

export const EDITOR_DEFAULT_CONTENT = '<h1></h1><p></p>';

export const TextEditor: FunctionComponent<TextEditorProps> = memo(
  ({ content, onUpdateHandler, editorRef, onPasteHandler, isShortPost = false }) => {
    const { t } = useTranslation();

    const [isReady, setIsReady] = useState(false);

    const [tableMenuSettings, setTableMenuSettings] = useState<TableMenuSettings>({
      top: 0,
      left: 0,
      visible: false,
    });

    const onTableSelection = useCallback(({ editor }: { editor: Editor }) => {
      if (!editor.isActive('table')) {
        setTableMenuSettings((settings) => ({ ...settings, visible: false }));
        return;
      }

      const { left, top } = editor.view.coordsAtPos(editor.state.selection.from);

      setTableMenuSettings({ top: top + 30, left: left - 15, visible: true });
    }, []);

    const placeholderSetup = useCallback(
      ({ editor, node }: { editor: Editor; node: Node }) => {
        if (!isShortPost) {
          if (node.type.name === 'paragraph') {
            return t('richTextEditor.paragraph-config-placeholder');
          }

          if (node.type.name === 'heading') {
            return t('richTextEditor.header-config-placeholder');
          }

          return '';
        }

        if (
          editor.state.doc.content.content.length === 1 &&
          editor.isEmpty &&
          node.type.name === 'paragraph'
        ) {
          return t('addContent.short-post-text');
        }

        return '';
      },
      [isShortPost, t]
    );

    const onMenuShouldShow = useCallback(({ selection }: EditorState) => {
      const { $from, empty } = selection;

      const node = $from.node();

      if (empty || node.type.name === 'doc') {
        return false;
      }

      return true;
    }, []);

    const dropPasteNode = useCallback(({ state }: EditorView, file: File) => {
      const { schema } = state;

      const { type, name } = file;

      const extension = name.slice(name.lastIndexOf('.')).toLowerCase();

      const docExtensions = FileAcceptType.FILE.split(',');

      const baseAttrs = { items: [], pastedFiles: [file] };

      if (type.startsWith('image/')) {
        return schema.nodes.imageNode.create({
          ...baseAttrs,
          withBorder: false,
          withBackground: false,
          stretched: false,
        });
      } else if (type.startsWith('audio/')) {
        return schema.nodes.audioNode.create(baseAttrs);
      } else if (type.startsWith('video/')) {
        return schema.nodes.videoNode.create(baseAttrs);
      } else if (type === 'application/pdf') {
        return schema.nodes.pdfNode.create(baseAttrs);
      } else if (docExtensions.includes(extension)) {
        return schema.nodes.fileNode.create(baseAttrs);
      }

      return null;
    }, []);

    const dropPasteMediaHandler = useCallback(
      ({ view, event, files }: DropPasteMediaHandlerArgs) => {
        if (!files.length) {
          return false;
        }

        const { state } = view;

        const pos =
          event instanceof DragEvent
            ? view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos
            : state.selection.from;

        if (pos == null) {
          return false;
        }

        const { schema, tr } = view.state;

        const isGallery = files.every((file) => file.type.startsWith('image/'));

        if (isGallery) {
          view.dispatch(
            tr.insert(
              pos,
              schema.nodes.imageNode.create({
                items: [],
                pastedFiles: files,
                withBorder: false,
                withBackground: false,
                stretched: false,
              })
            )
          );
          return true;
        }

        let transaction = tr;

        let insertPosition = pos;

        for (const file of files) {
          const node = dropPasteNode(view, file);

          if (node) {
            transaction = transaction.insert(insertPosition, node);
            insertPosition += node.nodeSize;
          }
        }

        view.dispatch(transaction);

        return true;
      },
      [dropPasteNode]
    );

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          ...(isShortPost && { heading: false, bulletList: false, orderedList: false }),
          link: { openOnClick: false },
          trailingNode: { node: 'paragraph' },
        }),
        Placeholder.configure({
          placeholder: placeholderSetup,
          showOnlyCurrent: isShortPost,
          showOnlyWhenEditable: true,
        }),
        Highlight,
        Subscript,
        Superscript,
        TaskList,
        TaskItem.configure({ nested: false }),
        Table.configure({ resizable: false, lastColumnResizable: false, cellMinWidth: 100 }),
        TableRow,
        TableHeader,
        TableCell,
        //custom nodes
        DelimiterNode,
        EmbedLinkNode,
        QuoteNode,
        CodeNode,
        CalloutNode,
        UserNode,
        StoryNode,
        StoryCardNode,
        ImageNode,
        AudioNode,
        VideoNode,
        PdfNode,
        FileNode,
      ],
      content,
      onSelectionUpdate: ({ editor }) => onTableSelection({ editor }),
      editorProps: {
        handleKeyDown({ state }, event) {
          const { $from } = state.selection;

          const node = $from.node();

          if ((event.key === 'Enter' && event.shiftKey) || node.type.name === 'doc') {
            event.preventDefault();

            return true;
          }

          return false;
        },
        handleDrop(view, event) {
          if (isShortPost) {
            return false;
          }

          const posAtCoords = editor.view.posAtCoords({ left: event.clientX, top: event.clientY });

          if (!posAtCoords) {
            return;
          }

          if (editor.state.doc.resolve(posAtCoords.pos).depth > 1) {
            return true;
          }

          const hasFiles = event.dataTransfer?.files?.length;

          if (!hasFiles) {
            return false;
          }

          dropPasteMediaHandler({ view, event, files: Array.from(event.dataTransfer.files) });
        },
        handlePaste(view, event) {
          const { state } = view;

          if (state.doc.resolve(state.selection.from).depth > 1) {
            return true;
          }

          onPasteHandler?.(event.clipboardData?.getData('text/plain') ?? '');

          if (isShortPost) {
            return false;
          }

          const files = Array.from(event.clipboardData?.items || [])
            .map((item) => item.getAsFile())
            .filter((file) => Boolean(file)) as File[];

          dropPasteMediaHandler({ view, event, files });
        },
      },
      onCreate({ editor }) {
        setIsReady(true);

        if (editor.getHTML() === EDITOR_DEFAULT_CONTENT) {
          editor.commands.focus('end');
        }
      },
      onUpdate({ editor }) {
        if (!onUpdateHandler) {
          return;
        }

        onUpdateHandler(
          htmlToMarkdown(
            editor
              .getHTML()
              .replace(/<p>(.*?)<\/p>/g, '$1\n')
              .trim() ?? ''
          )
        );
      },
    });

    useEffect(() => {
      if (editor && editorRef) {
        editorRef.current = editor;
      }
    }, [editor, editorRef]);

    if (!editor || !editor.view || !isReady) {
      return null;
    }

    return (
      <div
        className={classNames(classes['text-editor'], {
          [classes['text-editor--full-post']]: !isShortPost,
        })}
      >
        {!isShortPost && (
          <>
            <BlockTools editor={editor} />
            <DragHandle editor={editor}>
              <IconLabel
                iconId={'drag'}
                iconSize={20}
                className={classes['text-editor__drag-handle']}
              />
            </DragHandle>
          </>
        )}

        <EditorContent editor={editor} className={classNames({ scrollable: !isShortPost })} />

        <BubbleMenu
          editor={editor}
          options={{ arrow: false }}
          shouldShow={({ state }: { state: EditorState }) => onMenuShouldShow(state)}
        >
          <InlineTools editor={editor} isShortPost={isShortPost} />
        </BubbleMenu>

        <TableMenu editor={editor} settings={tableMenuSettings} />
      </div>
    );
  }
);
